/**
 * WebVTT, read for the transcript.
 *
 * The browser plays the captions itself (a `<track>`); this reads the same file
 * a second time so the lines can sit beside the video, be searched, and be
 * clicked to seek. Only what a transcript needs: cue times and text. Styling
 * tags (`<b>`, `<v Name>`) are stripped, and a malformed block is skipped rather
 * than failing the page.
 */

export interface Cue {
  start: number
  end: number
  text: string
}

const TIME = /(?:(\d+):)?(\d{1,2}):(\d{2})[.,](\d{3})/

function seconds(match: RegExpMatchArray): number {
  const [, hours, minutes, secs, millis] = match
  return Number(hours ?? 0) * 3600 + Number(minutes) * 60 + Number(secs) + Number(millis) / 1000
}

export function parseVtt(text: string): Cue[] {
  const cues: Cue[] = []
  for (const block of text.replace(/\r\n?/g, '\n').split(/\n{2,}/)) {
    const lines = block.split('\n')
    const timing = lines.findIndex((line) => line.includes('-->'))
    if (timing === -1) continue
    const [from, to] = (lines[timing] ?? '').split('-->')
    const start = TIME.exec(from ?? '')
    const end = TIME.exec(to ?? '')
    if (!start || !end) continue
    const body = lines
      .slice(timing + 1)
      .join(' ')
      .replace(/<[^>]+>/g, '')
      .replace(/\s+/g, ' ')
      .trim()
    if (body) cues.push({ start: seconds(start), end: seconds(end), text: body })
  }
  return cues
}

/** `75` → `1:15`; `3725` → `1:02:05`. */
export function clock(totalSeconds: number): string {
  const whole = Math.max(0, Math.floor(totalSeconds))
  const h = Math.floor(whole / 3600)
  const m = Math.floor((whole % 3600) / 60)
  const s = whole % 60
  const pad = (n: number) => String(n).padStart(2, '0')
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`
}

/** The index of the last item that starts at or before `time`; `-1` before the first. */
export function indexAt(items: readonly { start: number }[], time: number): number {
  let found = -1
  for (let i = 0; i < items.length; i += 1) {
    if ((items[i]?.start ?? Infinity) <= time) found = i
    else break
  }
  return found
}
